<template>
  <div class="list-page-container">
    <!-- 顶部操作区：副标题与创建按钮 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-upload"></i>
          <span>{{ $t('otaManagement.firmwareManagementTips') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button type="primary" @click="handleAdd" v-if="hasPermission('sys:ota:save')">
            <i class="el-icon-plus"></i> {{ $t('otaManagement.addNew') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 统一控制中心：筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <el-form :inline="true" :model="searchForm" class="filter-form" @submit.native.prevent>
          <el-form-item>
            <el-input
              v-model="searchForm.firmwareName"
              :placeholder="$t('otaManagement.searchPlaceholder')"
              clearable
              class="filter-input"
              @keyup.enter.native="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-search"></i>
            </el-input>
          </el-form-item>
          <div class="search-btns">
            <el-button type="primary" @click="handleSearch" icon="el-icon-search">{{ $t('common.search') }}</el-button>
            <el-button @click="handleReset" icon="el-icon-refresh">{{ $t('common.reset') }}</el-button>
          </div>
        </el-form>
      </div>
    </div>

    <!-- 数据列表 -->
    <el-card class="list-card" shadow="never">
      <el-table
        :data="firmwareList"
        v-loading="loading"
        style="width: 100%"
        height="100%"
        @selection-change="handleSelectionChange"
        class="custom-table"
      >
        <el-table-column type="selection" width="55" align="center"></el-table-column>
        <el-table-column prop="firmwareName" :label="$t('otaManagement.firmwareName')" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="type" :label="$t('otaManagement.firmwareType')" width="180" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" effect="plain">{{ getFirmwareTypeName(scope.row.type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="version" :label="$t('otaManagement.version')" width="100" align="center"></el-table-column>
        <el-table-column prop="size" :label="$t('otaManagement.fileSize')" width="120" align="center">
          <template slot-scope="scope">
            {{ formatFileSize(scope.row.size) }}
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('otaManagement.remark')" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="createDate" :label="$t('otaManagement.createTime')" width="170" align="center">
          <template slot-scope="scope">
            <span class="time-text">{{ formatDate(scope.row.createDate) }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="hasAnyActionPermission" :label="$t('otaManagement.actions')" :width="actionColumnWidth" align="center" fixed="right">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button class="btn-action-download" size="mini" plain icon="el-icon-download" @click="downloadFirmware(scope.row)" v-if="hasPermission('sys:ota:download')">
                {{ $t('otaManagement.download') }}
              </el-button>
              <el-button type="warning" size="mini" plain icon="el-icon-edit" @click="handleEdit(scope.row)" v-if="hasPermission('sys:ota:update')">
                {{ $t('common.edit') }}
              </el-button>
              <el-button type="danger" size="mini" plain icon="el-icon-delete" @click="handleDelete(scope.row)" v-if="hasPermission('sys:ota:delete')">
                {{ $t('common.delete') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      ></el-pagination>
    </div>

    <!-- 底部悬浮批量操作栏 -->
    <transition name="el-zoom-in-bottom">
      <div v-if="selectedItems.length > 0" class="batch-footer-bar">
        <div class="batch-info">
          <i class="el-icon-info"></i>
          <span>{{ $t('userManagement.selectedCount', { count: selectedItems.length }) }}</span>
        </div>
        <div class="batch-buttons">
          <el-button type="danger" size="small" icon="el-icon-delete" @click="handleBatchDelete" v-if="hasPermission('sys:ota:delete')">
            {{ $t('common.batchDelete') }}
          </el-button>
          <el-divider direction="vertical"></el-divider>
          <el-button type="text" @click="selectedItems = []">{{ $t('common.cancel') }}</el-button>
        </div>
      </div>
    </transition>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="550px" @open="handleDialogOpen">
      <el-form :model="firmwareForm" :rules="rules" ref="firmwareForm" label-width="100px">
        <el-form-item :label="$t('firmwareDialog.firmwareName')" prop="firmwareName">
          <el-input v-model="firmwareForm.firmwareName" :placeholder="$t('firmwareDialog.firmwareNamePlaceholder')"></el-input>
        </el-form-item>
        <el-form-item :label="$t('firmwareDialog.firmwareType')" prop="type">
          <el-select v-model="firmwareForm.type" :placeholder="$t('firmwareDialog.firmwareTypePlaceholder')" style="width: 100%" filterable :disabled="!!firmwareForm.id">
            <el-option v-for="item in firmwareTypes" :key="item.key" :label="item.name" :value="item.key"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('firmwareDialog.version')" prop="version">
          <el-input v-model="firmwareForm.version" :placeholder="$t('firmwareDialog.versionPlaceholder')"></el-input>
        </el-form-item>
        <el-form-item :label="$t('firmwareDialog.firmwareFile')" prop="firmwarePath">
          <el-upload
            ref="upload"
            action="#"
            :http-request="handleUpload"
            :before-upload="beforeUpload"
            :accept="'.bin,.apk,.wav'"
            :limit="1"
            :on-remove="handleRemove"
            :auto-upload="true"
          >
            <el-button size="small" type="primary" icon="el-icon-upload2">{{ $t('firmwareDialog.clickUpload') }}</el-button>
            <div slot="tip" class="el-upload__tip">{{ $t('firmwareDialog.uploadTip') }}</div>
          </el-upload>
          <el-progress v-if="isUploading || uploadStatus === 'success'" :percentage="uploadProgress" :status="uploadStatus"></el-progress>
          <div class="upload-hint" v-if="!firmwareForm.id">
            <i class="el-icon-warning-outline"></i> {{ $t('firmwareDialog.uploadHint') }}
          </div>
        </el-form-item>
        <el-form-item :label="$t('firmwareDialog.remark')" prop="remark">
          <el-input v-model="firmwareForm.remark" type="textarea" :rows="3" :placeholder="$t('firmwareDialog.remarkPlaceholder')"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitLoading">{{ $t('common.save') }}</el-button>
      </div>
    </el-dialog>

    <!-- 下载进度弹窗 -->
    <download-dialog ref="downloadDialog"></download-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import { hasPermission } from '@/utils/permission'
import { formatDate, formatFileSize } from '@/utils/index'
import DownloadDialog from '@/components/DownloadDialog.vue'

export default {
  name: 'OtaManagement',
  components: {
    DownloadDialog
  },
  data() {
    return {
      loading: false,
      submitLoading: false,
      isUploading: false,
      uploadProgress: 0,
      uploadStatus: '',
      firmwareList: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      searchForm: {
        firmwareName: ''
      },
      selectedItems: [],
      dialogVisible: false,
      dialogTitle: '',
      firmwareForm: {
        id: null,
        firmwareName: '',
        type: '',
        version: '',
        size: 0,
        remark: '',
        firmwarePath: ''
      },
      firmwareTypes: []
    }
  },
  computed: {
    // 操作列权限判断
    canDownloadFirmware() {
      return this.hasPermission('sys:ota:download')
    },
    canEditFirmware() {
      return this.hasPermission('sys:ota:update')
    },
    canDeleteFirmware() {
      return this.hasPermission('sys:ota:delete')
    },
    // 操作列可见按钮数量（基于权限）
    visibleActionCount() {
      let count = 0
      if (this.canDownloadFirmware) count += 1
      if (this.canEditFirmware) count += 1
      if (this.canDeleteFirmware) count += 1
      return count
    },
    // 是否有任何操作权限
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    // 操作列动态宽度
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170, 3: 240 }
      return widthMap[count] || 0
    },
    rules() {
      return {
        firmwareName: [{ required: true, message: this.$t('firmwareDialog.requiredFirmwareName'), trigger: 'blur' }],
        type: [{ required: true, message: this.$t('firmwareDialog.requiredFirmwareType'), trigger: 'change' }],
        version: [
          { required: true, message: this.$t('firmwareDialog.requiredVersion'), trigger: 'blur' },
          { pattern: /^\d+\.\d+\.\d+$/, message: this.$t('firmwareDialog.versionFormatError'), trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.getList()
    this.getFirmwareTypes()
  },
  methods: {
    hasPermission,
    formatDate,
    formatFileSize,
    async getList() {
      this.loading = true
      try {
        const res = await request({
          url: '/otaMag/page',
          method: 'get',
          params: {
            page: this.currentPage,
            limit: this.pageSize,
            firmwareName: this.searchForm.firmwareName
          }
        })
        if (res) {
          this.firmwareList = res.list || []
          this.total = res.total || 0
        }
      } catch (error) {
        console.error('Fetch OTA list error:', error)
      } finally {
        this.loading = false
      }
    },
    async getFirmwareTypes() {
      try {
        const res = await request({
          url: '/admin/dict/data/type/FIRMWARE_TYPE',
          method: 'get'
        })
        if (res) {
          this.firmwareTypes = res
        }
      } catch (error) {
        console.error('Fetch firmware types error:', error)
      }
    },
    getFirmwareTypeName(type) {
      const found = this.firmwareTypes.find(t => t.key === type)
      return found ? found.name : type
    },
    handleSearch() {
      this.currentPage = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.firmwareName = ''
      this.handleSearch()
    },
    handleSelectionChange(val) {
      this.selectedItems = val
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    handleAdd() {
      this.dialogTitle = this.$t('otaManagement.addFirmware')
      this.firmwareForm = {
        id: null,
        firmwareName: '',
        type: '',
        version: '',
        size: 0,
        remark: '',
        firmwarePath: ''
      }
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.dialogTitle = this.$t('otaManagement.editFirmware')
      this.firmwareForm = { ...row }
      this.dialogVisible = true
    },
    handleDialogOpen() {
      this.uploadProgress = 0
      this.uploadStatus = ''
      this.isUploading = false
      this.$nextTick(() => {
        if (this.$refs.firmwareForm) {
          this.$refs.firmwareForm.clearValidate()
        }
        if (this.$refs.upload) {
          this.$refs.upload.clearFiles()
        }
      })
    },
    beforeUpload(file) {
      const isValidSize = file.size / 1024 / 1024 < 100
      const isValidType = ['.bin', '.apk', '.wav'].some(ext => file.name.toLowerCase().endsWith(ext))
      if (!isValidType) {
        this.$message.error(this.$t('firmwareDialog.invalidFileType'))
        return false
      }
      if (!isValidSize) {
        this.$message.error(this.$t('firmwareDialog.invalidFileSize'))
        return false
      }
      return true
    },
    async handleUpload(options) {
      const { file } = options
      this.uploadProgress = 0
      this.uploadStatus = ''
      this.isUploading = true
      
      const formData = new FormData()
      formData.append('file', file)
      
      try {
        const res = await request({
          url: '/otaMag/upload',
          method: 'post',
          data: formData,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            }
          }
        })
        if (res) {
          this.firmwareForm.firmwarePath = res
          this.firmwareForm.size = file.size
          this.uploadStatus = 'success'
          this.$message.success(this.$t('firmwareDialog.uploadSuccess'))
        }
      } catch (error) {
        this.uploadStatus = 'exception'
        console.error('Upload firmware error:', error)
      } finally {
        setTimeout(() => {
          this.isUploading = false
        }, 2000)
      }
    },
    handleRemove() {
      this.firmwareForm.firmwarePath = ''
      this.firmwareForm.size = 0
    },
    async submitForm() {
      this.$refs.firmwareForm.validate(async (valid) => {
        if (valid) {
          if (!this.firmwareForm.id && !this.firmwareForm.firmwarePath) {
            this.$message.error(this.$t('firmwareDialog.requiredFirmwareFile'))
            return
          }
          this.submitLoading = true
          try {
            const isEdit = !!this.firmwareForm.id
            const res = await request({
              url: `/otaMag/${isEdit ? 'update' : 'save'}`,
              method: isEdit ? 'put' : 'post',
              data: this.firmwareForm
            })
            if (res !== undefined) {
              this.$message.success(isEdit ? this.$t('otaManagement.updateSuccess') : this.$t('otaManagement.addSuccess'))
              this.dialogVisible = false
              this.getList()
            }
          } catch (error) {
            console.error('Submit firmware error:', error)
          } finally {
            this.submitLoading = false
          }
        }
      })
    },
    handleDelete(row) {
      this.$confirm(this.$t('common.confirmDelete'), this.$t('common.tips'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(async () => {
        try {
          const res = await request({
            url: '/otaMag/delete',
            method: 'post',
            data: [row.id]
          })
          if (res !== undefined) {
            this.$message.success(this.$t('common.deleteSuccess'))
            this.getList()
          }
        } catch (error) {
          console.error('Delete firmware error:', error)
        }
      })
    },
    handleBatchDelete() {
      const ids = this.selectedItems.map(item => item.id)
      this.$confirm(this.$t('otaManagement.confirmBatchDelete', { paramCount: ids.length }), this.$t('common.tips'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(async () => {
        try {
          const res = await request({
            url: '/otaMag/delete',
            method: 'post',
            data: ids
          })
          if (res !== undefined) {
            this.$message.success(this.$t('otaManagement.batchDeleteSuccess', { paramCount: ids.length }))
            this.selectedItems = []
            this.getList()
          }
        } catch (error) {
          console.error('Batch delete firmware error:', error)
        }
      })
    },
    async downloadFirmware(row) {
      this.$refs.downloadDialog.show(row)
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/list-page.scss";

/* OTA管理页面特有样式 */
.list-page-container {
  .delete-btn {
    color: #ef4444;
    &:hover {
      color: #dc2626;
    }
  }
}

::v-deep {
  .el-dialog {
    border-radius: 12px;
    overflow: hidden;
    
    .el-dialog__header {
      background-color: #f8fafc;
      padding: 15px 20px;
      border-bottom: 1px solid #f1f5f9;
      
      .el-dialog__title {
        font-size: 15px;
        font-weight: 600;
        color: #1e293b;
      }
    }
    
    .el-dialog__body {
      padding: 20px 25px;
    }
    
    .el-dialog__footer {
      padding: 15px 20px;
      border-top: 1px solid #f1f5f9;
    }

    .el-input__inner, .el-textarea__inner, .el-select {
      border-radius: 8px;
    }
  }
}
</style>
